/** @jsxImportSource @emotion/react */
import Root from "pages/root";
import {
  BottonContainer,
  Container,
  DeleteBtn,
  EditContainer,
  IconContainer,
  ProfileContainer,
  ProfileImg,
  Rectangle,
  TitleContainer,
} from "./index.styles";
import UI from "@mile-meeting/mile-ui";
import { css } from "@emotion/react";
import { useEffect, useRef, useState } from "react";
import WorkspaceDeleteModal from "./workspace-delete-modal";
import { useLocation, useNavigate } from "react-router-dom";
import { uploadImageToS3 } from "apis/uploadImage";
import { updateWorkspaceInfo } from "apis/workspace";
import { useToast } from "hook/useToast";
import { checkMemberDeletionPermission } from "apis/auth/member";
import { useRecoilValue } from "recoil";
import { member } from "recoil/recoilState";

const WorkspaceManageEdit = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [workspaceName, setWorkspaceName] = useState<string>("");
  const [workspaceImage, setWorkspaceImage] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const memberInfo = useRecoilValue(member);

  const onHandleIconClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onHandleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const previewUrl = URL.createObjectURL(file);
      setWorkspaceImage(previewUrl);
    }
  };

  const uploadImage = async (image?: string) => {
    if (!image) {
      return null;
    }

    // blob URL을 fetch하여 Blob 파일로 변환
    if (typeof image === "string" && image.startsWith("blob:")) {
      const response = await fetch(image);
      const blob = await response.blob();
      const formData = new FormData();
      formData.append("file", blob);
      const imageUrl = await uploadImageToS3(formData);
      return imageUrl;
    }

    // image가 string일 경우 그대로 반환 (이미 업로드된 URL일 가능성)
    if (typeof image === "string") return image;
  };

  const onHandleSave = async () => {
    if (workspaceName.length === 0) {
      showToast("워크스페이스 이름을 입력해주세요.", "warning");
      return;
    }

    let imageUrl = (await uploadImage(workspaceImage)) ?? "";
    const res = await updateWorkspaceInfo(workspaceName, imageUrl);
    if (res.result) {
      navigate(`/setting/${encodeURIComponent("워크스페이스 관리")}`, { state: { updateSuccess: true } });
    } else {
      showToast("워크스페이스 정보 수정에 실패했습니다. 다시 시도해주세요.", "error");
    }
  };

  useEffect(() => {
    if (location.state) {
      setWorkspaceName(location.state["workspaceName"]);
      if (location.state["workspaceImage"] !== "") {
        setWorkspaceImage(location.state["workspaceImage"]);
      }
    }
  }, []);

  const onHandleDelete = async () => {
    const res = await checkMemberDeletionPermission();
    if (res.success) {
      if (res.data) setOpenModal(true);
      else showToast("다른 멤버에게 Owner 권한을 부여한 뒤, 워크스페이스 나가기를 재시도해 주세요.", "warning");
    } else {
      showToast(res.message ?? "처리 중 오류가 발생했습니다. 다시 시도해 주세요.", "error");
    }
  };

  return (
    <Container>
      <TitleContainer>
        <UI.Typography
          type="title03"
          fontStyle="bold"
          color="var(--alias-color-label-netural)"
          style={{ padding: "24px 20px" }}
        >
          워크스페이스 관리
        </UI.Typography>
        <div style={{ padding: "8px 20px", display: "flex", gap: 8 }}>
          <UI.Button title="나가기" type="outlined" onClick={() => navigate("/setting/워크스페이스%20관리")} />
          <UI.Button title="저장하기" onClick={onHandleSave} disabled={workspaceName.length < 1} />
        </div>
      </TitleContainer>
      <Rectangle />
      <ProfileContainer>
        <div
          css={css`
            position: relative;
          `}
        >
          <ProfileImg
            src={workspaceImage ?? "https://mile-storage.s3.ap-northeast-2.amazonaws.com/web/mile-logo-gray.png"}
          />
          <IconContainer onClick={onHandleIconClick}>
            <UI.IconButton.Background iconSize={24} icon="modify" />
          </IconContainer>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept="image/*"
            onChange={onHandleFileChange}
          />
        </div>
        <UI.Typography type="label02" fontStyle="regular" color="var(--alias-color-label-alternative)">
          * 1:1 비율, 권장 규격: 240 x 240px
        </UI.Typography>
      </ProfileContainer>
      <EditContainer>
        <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative">
          워크스페이스 이름
        </UI.Typography>
        <UI.Input
          maxLength={50}
          value={workspaceName}
          width={400}
          showHelpText
          helpText="회사나 조직의 이름을 작성해 주세요."
          showMaxLength={true}
          onChange={(value) => value.length <= 50 && setWorkspaceName(value)}
        />
      </EditContainer>
      {memberInfo && memberInfo.role === 0 && (
        <BottonContainer>
          <DeleteBtn>
            <UI.ButtonGroups.Text
              type="negative"
              onClick={onHandleDelete}
              title="워크스페이스 삭제"
              size="medium"
              prefixIcon="trashCan"
              showPrefixIcon
            />
          </DeleteBtn>
        </BottonContainer>
      )}
      <WorkspaceDeleteModal modalIsOpen={openModal} closeModal={() => setOpenModal(false)} />
    </Container>
  );
};

export default WorkspaceManageEdit;
